import { Component, ElementRef, Inject, OnInit, Renderer2 } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { ConfirmationDialogueComponent } from 'app/components/application/common/confirmation-dialogue/confirmation-dialogue.component';
import { IPerson } from 'app/models/People/person';
import { IPersonAddress } from 'app/models/People/personAddress';
import { IAtpCourse } from 'app/models/atp/atpCourse';
import { IAtpCourseStudent } from 'app/models/atp/atpCourseStudent';
import { IAddress } from 'app/models/common/address';
import { IPersonFilter } from 'app/models/filters/personFilter';
import { AtpService } from 'app/services/atp/atp.service';
import { CommonDataService } from 'app/services/common/common-data.service';
import { SharedService } from 'app/services/core/shared.service';
import { PersonService } from 'app/services/person/person.service';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AddEditAtpComponent } from '../add-edit-atp.component';
import { AddEditAtpCourseComponent } from '../add-edit-atp-course/add-edit-atp-course.component';

@Component({
  selector: 'app-add-edit-course-student',
  templateUrl: './add-edit-course-student.component.html',
  styleUrls: ['./add-edit-course-student.component.scss']
})
export class AddEditCourseStudentComponent implements OnInit {
  public courseStudentDataForm = null;
  public courseStudentData: IAtpCourseStudent = null;  
  public address: IAddress = {id: 0, state: '', street: '', city: '', contactType: '', county: '', zip: ''};
  public studentAddress: IPersonAddress = {id: 0, person: null, address: this.address, isCurrentMailing: true, isCurrentPhysical: false}  
  public uiData = LEAD_BASED_PAINT;
  private readonly debounceString: Subject<string> = new Subject<string>();
  public isPendingStudent: boolean = false;
  
  public studentList: IPerson[] = [];
  public filteredStudentList: IPerson[] = [];
  public selectedStudent: IPerson = null;
  public createNew = 'Create a New Person'
  public personDataForm = null;
  public studentFilter: IPersonFilter = {filterAffiliation: [], filterStatus: '', filterString: '', expirationStartDate: '', expirationEndDate: '', filterDiscipline: []};

  constructor(private readonly personService:PersonService,
    @Inject(MAT_DIALOG_DATA) public data:{student:IAtpCourseStudent, course:IAtpCourse, courseStudents:IAtpCourseStudent[], studentId: number},
    public sharedService: SharedService,
    private readonly atpService: AtpService,
    private readonly commonService: CommonDataService,
    private readonly toastr: ToastrService,   
    public dialog: MatDialog, 
    private readonly dialogRef: MatDialogRef<AddEditCourseStudentComponent>,
    private readonly renderer: Renderer2, 
    private readonly el: ElementRef) 
  {     
    this.data.studentId = this.data.studentId || 0;    
    this.initializeDebounceFunction();    
  }
  
  ngOnInit(): void {
    this.setCourseDataFormFields();
    if(this.data.student !== null && this.data.student !== undefined){
      // Check if this is a pending student
      this.isPendingStudent = (this.data.student as any).status === 'Pending';
      
      // For pending students, create a temporary student object from pending data
      if (this.isPendingStudent) {
        const pendingData = this.data.student as any;
        this.selectedStudent = {
          id: 0,
          firstName: pendingData.firstName || '',
          lastName: pendingData.lastName || '',
          dateOfBirth: pendingData.dateOfBirth,
          title: '',
          middleName: '',
          ssn: '',
          inactive: false,
          response: '',
          certificates: null
        };
        this.studentList = [this.selectedStudent];
        this.filteredStudentList = this.studentList;
      } else {
        this.studentList = [this.data.student.student];
        this.filteredStudentList = this.studentList;
        this.selectedStudent = this.data.student.student;   
      }
      this.updateScrollContainerHeight();   
      this.loadData();
    }
    else if(this.data.studentId > 0){            
      this.atpService.getATPCourseStudentById(this.data.studentId).subscribe(result=>{
        console.log(result)
        this.data.student = result;
        this.data.course = result.atpCourse;
        this.studentList = [result.student];
        this.selectedStudent = result.student
        this.filteredStudentList = this.studentList;
        this.updateScrollContainerHeight();   
        this.loadData();
      },error=>{this.toastr.error("There was an error getting the course student: ", error);
        this.resetStudentList();
        this.loadData();
      })      
    }
    else{
      this.resetStudentList();
      this.loadData();
    }    
  }

  initializeDebounceFunction(): void {
    this.debounceString.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(value => {      
      this.filterStaff(value.trim().toLowerCase());     
    });
  }

  loadData(){
    // Handle pending students differently
    if(this.isPendingStudent){
      if(this.data.student !== null){
        this.courseStudentData = this.data.student;
        
        // Load pending student data directly into form (firstName, lastName, etc. are on the courseStudentData for pending)
        const pendingData = this.data.student as any;
        this.courseStudentDataForm.controls['courseCertificateNumber'].setValue(pendingData.courseCertificateNumber || '');
        this.courseStudentDataForm.controls['testScore'].setValue(pendingData.testScore);
        this.courseStudentDataForm.controls['photoImage'].setValue(pendingData.photoImage || '');
        this.courseStudentDataForm.controls['firstName'].setValue(pendingData.firstName || '');
        this.courseStudentDataForm.controls['lastName'].setValue(pendingData.lastName || '');
        this.courseStudentDataForm.controls['dateOfBirth'].setValue(pendingData.dateOfBirth);
        this.courseStudentDataForm.controls['street'].setValue(pendingData.street || '');
        this.courseStudentDataForm.controls['city'].setValue(pendingData.city || '');
        this.courseStudentDataForm.controls['state'].setValue(pendingData.state || 'OR');
        this.courseStudentDataForm.controls['zip'].setValue(pendingData.zip || '');
      }
    }
    else if(this.selectedStudent?.id >= 0){
      if(this.data.student !== null){
        this.courseStudentData = this.data.student; 
        this.courseStudentData.student = this.selectedStudent;
      }
      else{
        this.courseStudentData = {
          id: 0,
          atpCourse: this.data.course,
          student: this.selectedStudent,
          courseCertificateNumber: "",
          testScore: null,
          photoImage: "",
          paymentUpdate: true
        };     
      }
  
      this.personService.getPersonAddressesByPersonId(this.selectedStudent.id).subscribe(result=>{
        this.studentAddress = result.length > 0 ? result[0] : {id: 0, person: null, address: null, isCurrentMailing: true, isCurrentPhysical: false};
        this.address = this.studentAddress.id > 0 ? this.studentAddress.address : {id: 0, state: '', street: '', city: '', contactType: '', county: '', zip: ''};;
        for (const controlName in this.studentAddress?.address) {
          if (this.courseStudentDataForm.controls.hasOwnProperty(controlName)) {        
            this.courseStudentDataForm.controls[controlName].setValue(this.studentAddress.address[controlName]);          
          }
        }  
      },error=>{
        this.toastr.error("There was an error getting the student address: ", error);
      })
  
      for (const controlName in this.courseStudentData) {
        if (this.courseStudentDataForm.controls.hasOwnProperty(controlName)) {        
          this.courseStudentDataForm.controls[controlName].setValue(this.courseStudentData[controlName]);          
        }
      }  
  
      for (const controlName in this.courseStudentData.student) {
        if (this.courseStudentDataForm.controls.hasOwnProperty(controlName) && this.courseStudentData.student.id !== 0) {        
          this.courseStudentDataForm.controls[controlName].setValue(this.courseStudentData.student[controlName]);          
        }
      }  
    }        
  }

  setCourseDataFormFields(){       
    this.courseStudentDataForm = new UntypedFormGroup({
      courseCertificateNumber: new UntypedFormControl(""),
      testScore: new UntypedFormControl(""),
      photoImage: new UntypedFormControl(""),
      firstName: new UntypedFormControl(""),
      lastName: new UntypedFormControl(""),
      dateOfBirth: new UntypedFormControl(null),
      street: new UntypedFormControl(""),
      city: new UntypedFormControl(""),
      state: new UntypedFormControl(""),
      zip: new UntypedFormControl(""),
    }); 
  }

  updateCourseStudentData(): void {    
    for (const controlName in this.courseStudentData) {
      if (this.courseStudentDataForm.controls.hasOwnProperty(controlName)) {
        this.courseStudentData[controlName] = this.courseStudentDataForm.controls[controlName].value;        
      }
    } 

    // For pending students, update the pending-specific fields
    if (this.isPendingStudent) {
      (this.courseStudentData as any).firstName = this.courseStudentDataForm.controls['firstName'].value;
      (this.courseStudentData as any).lastName = this.courseStudentDataForm.controls['lastName'].value;
      (this.courseStudentData as any).dateOfBirth = this.courseStudentDataForm.controls['dateOfBirth'].value;
      (this.courseStudentData as any).street = this.courseStudentDataForm.controls['street'].value;
      (this.courseStudentData as any).city = this.courseStudentDataForm.controls['city'].value;
      (this.courseStudentData as any).state = this.courseStudentDataForm.controls['state'].value;
      (this.courseStudentData as any).zip = this.courseStudentDataForm.controls['zip'].value;
    } else {
      for (const controlName in this.selectedStudent) {
        if (this.courseStudentDataForm.controls.hasOwnProperty(controlName)) {
          this.selectedStudent[controlName] = this.courseStudentDataForm.controls[controlName].value;        
        }
      }    

      for (const controlName in this.address) {
        if (this.courseStudentDataForm.controls.hasOwnProperty(controlName)) {
          this.address[controlName] = this.courseStudentDataForm.controls[controlName].value;        
        }
      }
    }    
  }
  
  savePrompt(){
    let isNew = this.courseStudentData.id === 0;
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',      
      data: {message: isNew ? 'Are you sure you want to <strong>Add</strong> the student?' : 'Are you sure you want to <strong>Update</strong> the student?',title: isNew ? 'Add the Student' : 'Update the Student'},});
    dialogRef.afterClosed().subscribe(result => {
      if(result){
        // For pending students, skip person/address save and go directly to save course student
        if(this.isPendingStudent){
          this.saveCourseStudent();
        } else {
          this.updateCourseStudentData();
          this.saveData();
        }
      }
    })
  }

  saveCourseStudent() {            
    if (this.courseStudentData.testScore !== null && this.courseStudentData.testScore.toString().trim() === "") {
      this.courseStudentData.testScore = null;
    }    
    
    // Update data from form
    this.updateCourseStudentData();
    
    // Use appropriate save method based on whether it's a pending student
    const saveObservable = this.isPendingStudent 
      ? this.atpService.saveATPCoursePendingStudent(this.courseStudentData)
      : this.atpService.saveATPCourseStudent(this.courseStudentData);
        
    saveObservable.subscribe(result=>{
      this.toastr.success("Success")
      this.dialogRef.close(true);
    },error=>{
      this.toastr.error("There was an error saving the course student: ", error)
    })
  }

  saveData(){
    if (this.courseStudentData.testScore !== null && this.courseStudentData.testScore.toString().trim() !== '' && (isNaN(this.courseStudentData.testScore) || this.courseStudentData.testScore < 70 || this.courseStudentData.testScore > 100)) {
      this.toastr.error("The student's test score must be between 70 to 100 to save the student.");
    }
    else{
      this.personService.savePerson(this.selectedStudent).subscribe(result=>{
        this.selectedStudent = result;
        this.studentAddress.person = this.selectedStudent;
        this.courseStudentData.student = this.selectedStudent;
  
        this.commonService.saveAddress(this.address).subscribe(result=>{
          this.address = result;
          this.studentAddress.address = this.address;
  
          if(this.studentAddress.id === 0){
            this.personService.savePersonAddress(this.studentAddress).subscribe(result=>{},error=>{this.toastr.error("There was an error saving the person address: ", error);})
          }        
          this.saveCourseStudent();
        },error=>{this.toastr.error("There was an error saving the address: ", error);})
      },error=>{this.toastr.error("There was an error saving the student: ", error);})
    }
  }

  updateDebounceString(event: any){    
    this.debounceString.next(event.target.value); 
    this.updateScrollContainerHeight();
  }
  
  resetStudentList(): void {
    this.studentList = [{id: 0, firstName: this.createNew, lastName: '', middleName: '', title: '', ssn: '', dateOfBirth: '', inactive: false, response: 'Unk', certificates: null}];        
    this.filteredStudentList = this.studentList;
  }

  updateScrollContainerHeight() {
    if (this.selectedStudent?.id >= 0 || this.data.studentId > 0) {
      const scrollContainer = this.el.nativeElement.querySelector('.scroll-container');
      this.renderer.setStyle(scrollContainer, 'height', '60vh');
      this.setCourseDataFormFields();
      this.loadData();
    }
    else {
      const scrollContainer = this.el.nativeElement.querySelector('.scroll-container');
      this.renderer.setStyle(scrollContainer, 'height', '10vh');
    }
  }
  
  filterStaff(value: any) {
    if (value.length < 3) {
      this.resetStudentList();      
    }
    else{
      this.studentFilter.filterString = value;
      this.personService.getPeopleFiltered(this.studentFilter).subscribe(result=>{
        this.studentList = result.filter(student => !this.data.courseStudents?.some(currentStudent => currentStudent.student.id === student.id));
        this.filteredStudentList = this.studentList;
      },error=>{this.toastr.error("An error occured getting the student list: ", error);});
    }
  }
  
displayStudent(staffMember: any): string {
    if (staffMember) {
        if (staffMember.id === 0) {
            return staffMember.firstName;
        } else {
            return `${staffMember.id}: ${staffMember.firstName} ${staffMember.lastName}`;
        }
    } else {
        return '';
    }
  }

  editATP(){
    const dialogRef = this.dialog.open(AddEditAtpComponent, {
      width: '90%',
      data: {atp: this.data.course.atp},});
    dialogRef.afterClosed().subscribe(result => {

    });
  }

  editCourse(){
    const dialogRef = this.dialog.open(AddEditAtpCourseComponent, {
      width: '90%',
      data: {course: this.data.course, atp: this.data.course.atp},});
    dialogRef.afterClosed().subscribe(result => {  
      
    });
  }
}
